import { useState } from "react";
import { Loader2, Lock, Users } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useChatHistory } from "@/contexts/ChatHistoryContext";
import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";

/**
 * Owner-only control to share a matter chat with the whole matter team (or make it private
 * again). The icon shows the *current* state (Users = shared, Lock = private) and the label
 * shows the *action* ("Make private" / "Share"). Renders nothing for teammates or until the
 * chat's metadata is known — the matter's Chats tab is the authoritative management surface.
 */
export function ShareChatToggle({ chatId, projectId }: { chatId: string; projectId: string }) {
  const { user } = useAuth();
  const { chats } = useChatHistory();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chat = chats.find((item) => item.id === chatId);
  if (!chat || !user || chat.user_id !== user.uid) return null;

  const shared = Boolean(chat.shared_at);

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await (shared ? api.chat.unshare(chatId) : api.chat.share(chatId));
    } catch {
      setError("Couldn't update sharing — try again.");
    } finally {
      // Resync from the server so the button always reflects the true shared state (a failed
      // toggle simply stays as it was).
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.chatsAll }),
        queryClient.invalidateQueries({ queryKey: qk.projectChats(projectId) }),
      ]);
      setBusy(false);
    }
  };

  return (
    <span className="flex items-center gap-2">
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
      <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void toggle()} title={shared ? "Shared with the matter team" : "Private to you"}>
        {busy ? <Loader2 className="size-4 animate-spin" /> : shared ? <Users className="size-4" /> : <Lock className="size-4" />}
        {shared ? "Make private" : "Share"}
      </Button>
    </span>
  );
}
